import type { RestRequestInit } from "./httpClient";

// NDJSON stays on native fetch: axios's fetch adapter buffers the body and breaks incremental delivery.
export async function* readNdjsonStream<T = unknown>(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<T, void, void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  let done = false;
  while (!done) {
    const { done: chunkDone, value } = await reader.read();
    done = chunkDone;

    buffer += decoder.decode(value, { stream: true });

    const lastNewline = buffer.lastIndexOf("\n");
    if (lastNewline < 0) continue;

    for (const line of buffer.slice(0, lastNewline).split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      yield JSON.parse(trimmed) as T;
    }
    buffer = buffer.slice(lastNewline + 1);
  }

  buffer += decoder.decode();
  const trailing = buffer.trim();
  if (trailing) {
    yield JSON.parse(trailing) as T;
  }
}

async function ndjsonRequest(path: string, init: RestRequestInit = {}): Promise<Response> {
  const { method = "GET", body, headers } = init;
  const hasBody = body !== undefined;

  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: {
      Accept: "application/x-ndjson",
      ...(hasBody && { "Content-Type": "application/json" }),
      ...headers,
    },
    body: hasBody ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }

  return response;
}

export async function* restNdjson<T>(
  path: string,
  init?: RestRequestInit,
): AsyncGenerator<T, void, void> {
  const response = await ndjsonRequest(path, init);

  if (!response.body) {
    throw new Error("Response has no body");
  }

  yield* readNdjsonStream<T>(response.body);
}
